import LocationDetailsV2 from '../location_details_v2';

class PostWizard {
  constructor() {
    const step = (document.querySelector('.lj-wizard-post') as HTMLElement).dataset.wizardStep;

    switch (step) {
      case 'generalInformation':
        this.generalInformation();
        break;
      case 'candidateBackground':
        this.candidateBackground();
        break;
      case 'locationDetails':
        this.locationDetails();
        break;
      case 'hiringProcess':
        this.hiringProcess();
        break;
      default:
        break;
    }

    // Scroll to first error if validation failed
    this.scrollToFirstError();

    // Clear a field's error as soon as it is corrected (ILJ-1094)
    this.clearErrorsOnEdit();
  }

  generalInformation() {
    const checkedInput: HTMLElement = document.querySelector('.lj-wizard-category > input:checked');
    if (checkedInput) {
      checkedInput.setAttribute('checked', 'true');
      checkedInput.parentElement.classList.add('lj-wizard-category--active');
      checkedInput.nextElementSibling.classList.add('lj-wizard-category--active');
      (checkedInput.nextElementSibling.querySelector('.checked') as HTMLElement).style.display =
        'block';
      (
        checkedInput.nextElementSibling.querySelector('.category-image') as HTMLElement
      ).style.display = 'none';
    }

    this.initTrixEditor();
    $(document.getElementById('job_ad_form_category_id')).selectize();

    let mustHaveSelectize: any = null;
    let niceToHaveSelectize: any = null;

    const languagesEl = document.getElementById('job_preferred_languages') as HTMLSelectElement | null;
    if (languagesEl) {
      const maxItems = parseInt(languagesEl.dataset.maxItems || '5', 10);
      mustHaveSelectize = this.initLanguagesSelectize(languagesEl, maxItems, 'wrapper-language');
    }

    const niceToHaveLanguagesEl = document.getElementById(
      'job_nice_to_have_languages'
    ) as HTMLSelectElement | null;
    if (niceToHaveLanguagesEl) {
      const maxItems = parseInt(niceToHaveLanguagesEl.dataset.maxItems || '5', 10);
      niceToHaveSelectize = this.initLanguagesSelectize(
        niceToHaveLanguagesEl,
        maxItems,
        'wrapper-nice-to-have-language'
      );
    }

    // A language must appear in only one of the two selects, so an option
    // chosen in one is hidden from the other's dropdown (and restored on removal).
    this.syncExclusiveLanguages(mustHaveSelectize, niceToHaveSelectize);
  }

  // Must have / Nice to have languages: keep the selectize a clean, empty search box
  // and render the selected languages as removable tags below it (mirrors the skills tags).
  initLanguagesSelectize(languagesEl: HTMLSelectElement, maxItems: number, wrapperId: string) {
    const wrapper = document.getElementById(wrapperId);
    let tagsContainer = wrapper?.querySelector('.lj-language-tags') as HTMLUListElement | null;
    if (wrapper && !tagsContainer) {
      tagsContainer = document.createElement('ul');
      tagsContainer.className = 'lj-language-tags';
      wrapper.appendChild(tagsContainer);
    }

    const renderTag = (selectize, value: string, label: string) => {
      if (!tagsContainer || tagsContainer.querySelector(`[data-value="${value}"]`)) return;

      const tag = document.createElement('li');
      tag.className = 'lj-language-tag';
      tag.dataset.value = value;
      tag.textContent = label;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'lj-language-tag-remove';
      remove.setAttribute('aria-label', `Remove ${label}`);
      remove.addEventListener('click', () => selectize.removeItem(value));

      tag.appendChild(remove);
      tagsContainer.appendChild(tag);
    };

    const $languages = $(languagesEl).selectize({
      plugins: ['remove_button'],
      maxItems,
      hideSelected: true,
      closeAfterSelect: true,
      onItemAdd(this: any, value: string) {
        const option = this.options[value];
        renderTag(this, value, option ? option.text : value);
        this.setTextboxValue('');
        this.refreshOptions(false);
      },
      onItemRemove(value: string) {
        const tag = tagsContainer?.querySelector(`[data-value="${value}"]`);
        tag?.parentNode?.removeChild(tag);
      },
    });

    // Languages already selected on load (editing a draft / returning to step 1)
    // don't trigger onItemAdd, so render their tags explicitly. renderTag is
    // idempotent, so this is safe even if selectize emits the event on init.
    const selectize = ($languages[0] as any)?.selectize;
    if (selectize) {
      selectize.items.forEach((value: string) => {
        const option = selectize.options[value];
        renderTag(selectize, value, option ? option.text : value);
      });
    }

    return selectize;
  }

  // Cross-wire the two language selectize instances so a value picked in one is
  // removed from the other's dropdown options, and restored when unpicked. Also
  // reconciles pre-filled values on load. No-op if either select is missing.
  syncExclusiveLanguages(mustHave: any, niceToHave: any) {
    if (!mustHave || !niceToHave) return;

    // Snapshot every option label up-front so we can restore an option after it
    // has been removed from a sibling (removal drops the option object).
    const labels: { [value: string]: string } = {};
    [mustHave, niceToHave].forEach((instance: any) => {
      Object.keys(instance.options).forEach((value: string) => {
        labels[value] = instance.options[value].text;
      });
    });

    const removeFromSibling = (sibling: any, value: string) => {
      // Guard is load-bearing: selectize's removeOption() also removes the item
      // if it's currently selected, so skip when the sibling already has it.
      if (sibling.items.indexOf(value) !== -1) return;
      if (sibling.options[value]) {
        sibling.removeOption(value);
        sibling.refreshOptions(false);
      }
    };

    const restoreToSibling = (sibling: any, value: string) => {
      if (sibling.items.indexOf(value) !== -1) return;
      if (!sibling.options[value]) {
        sibling.addOption({ value, text: labels[value] || value });
        sibling.refreshOptions(false);
      }
    };

    // Legacy drafts can have the same language selected in both lists. Enforce
    // exclusivity on load by keeping it on Must have and dropping it from Nice
    // to have, before syncing the dropdown options below.
    mustHave.items.slice().forEach((value: string) => {
      if (niceToHave.items.indexOf(value) !== -1) niceToHave.removeItem(value);
    });

    mustHave.items.forEach((value: string) => removeFromSibling(niceToHave, value));
    niceToHave.items.forEach((value: string) => removeFromSibling(mustHave, value));

    const wire = (source: any, target: any) => {
      source.on('item_add', (value: string) => {
        if (source.options[value]) labels[value] = source.options[value].text;
        removeFromSibling(target, value);
      });
      source.on('item_remove', (value: string) => {
        restoreToSibling(target, value);
      });
    };

    wire(mustHave, niceToHave);
    wire(niceToHave, mustHave);
  }

  candidateBackground() {
    $(document.getElementById('job_ad_form_certificate_options')).selectize();
  }

  properSanitize(elementValue: string) {
    const parsedHtml = $.parseHTML(elementValue);
    if (parsedHtml === null) {
      return elementValue;
    }
    return parsedHtml
      .filter(
        (item: HTMLElement | null) =>
          item.nodeName !== '#comment' || item.innerHTML !== '<!--block--><br>'
      )
      .map((item: HTMLElement | null) => {
        if (item.nodeName === 'FIGURE') {
          return item.children[0].outerHTML;
        }
        return item.outerHTML;
      })
      .join('');
  }

  submitTrixEditorForms(e: JQuery.ClickEvent) {
    e.preventDefault();
    const trixEditors: any = document.querySelectorAll('trix-editor');

    trixEditors.forEach(trix => {
      const str = trix.innerHTML;
      const key = trix.attributes.input.value;
      (document.getElementById(`job_ad_form_${key}`) as HTMLInputElement).value =
        this.properSanitize(str);
    });

    document.querySelector('form').submit();
  }

  // Delegates to the shared LocationDetailsV2 controller so the wizard and
  // the employer job ad edit form never carry duplicate, drifting show/hide
  // logic (ILJ-814/815). See frontend/components/post_a_job/location_details_v2.ts.
  locationDetails() {
    this.locationDetailsV2();
  }

  locationDetailsV2() {
    new LocationDetailsV2('work-model-options-radio').init();
  }

  hiringProcess() {
    this.initTrixEditor();
  }

  initTrixEditor() {
    const trixEditors = document.querySelectorAll('trix-editor');

    const updateNumberValue = editor => {
      // Editors without a character counter (e.g. Job description, ILJ-1088)
      // have no `.number[data-limit]` element — skip them.
      const num = editor.parentElement.querySelector('.number[data-limit]');
      if (!num) return;
      const limit = Number(num.dataset.limit);
      num.innerHTML = String(limit + 1 - editor.editor.getDocument().toString().length) + ' ';
    };

    for (const trix of trixEditors as any) {
      const key = (trix.attributes as any).input.value;
      const str = (document.getElementById(`job_ad_form_${key}`) as HTMLInputElement).value;

      if (str) {
        trix.editor.insertHTML(this.properSanitize(str));
      }

      updateNumberValue(trix);

      trix.onkeyup = e => {
        updateNumberValue(e.target);
      };

      // Disable file attachments 
      trix.addEventListener('trix-file-accept', (e) => {
        e.preventDefault();
      });

      // The submit step bypasses `.next-button` and calls `form.submit()`
      // directly, which fires no submit event, so serialising on the button
      // click alone loses whatever was typed. Mirror the editor into its hidden
      // input on every change instead, so any submit path carries it (ILJ-1386).
      trix.addEventListener('trix-change', () => {
        (document.getElementById(`job_ad_form_${key}`) as HTMLInputElement).value =
          this.properSanitize(trix.innerHTML);
      });
    }

    // Hide the file attachment button from Trix toolbar
    $('trix-toolbar button[data-trix-action="attachFiles"]').hide();
    
    // Hide the button group containing the attach button to remove visual artifacts
    $('trix-toolbar .trix-button-group:has(button[data-trix-action="attachFiles"])').hide();

    // Remove all trix toolbar elements
    $('.attachment__toolbar')?.remove();
    $('.attachment__caption--editing')?.remove();

    $('body').on('click', '.next-button', e => {
      this.submitTrixEditorForms(e);
    });
  }

  scrollToFirstError() {
    // Use shared scrollToFirstError utility function
    if (typeof (window as any).scrollToFirstError === 'function') {
      (window as any).scrollToFirstError({ delay: 300 });
    }
  }

  // Server-rendered per-field errors stay on screen until the next submit.
  // Clear a field's error message + error state as soon as the user edits it,
  // so corrected fields don't keep showing a red error (ILJ-1094). Delegated on
  // the wizard container so it covers text inputs, selects/selectize, radios
  // and the trix editor (which emits `trix-change`).
  clearErrorsOnEdit() {
    const root = document.querySelector('.lj-wizard-post');
    if (!root) {
      return;
    }

    const clearFor = (target: EventTarget | null) => {
      if (!(target instanceof Element)) {
        return;
      }
      const group = target.closest(
        '.lj-input-group, .lj-wizard-block-section, .lj-wizard-block, .lj-select-group'
      );
      if (!group) {
        return;
      }
      // Remove the error message elements.
      group.querySelectorAll('.lj-alert--error, .ld-error-alert').forEach(el => el.remove());
      // Strip the error state classes, both on the group itself (e.g. a
      // .lj-select-group / React wrapper carries the class directly) and on
      // any descendant input.
      group.classList.remove('lj-input--error', 'ld-error-input');
      group
        .querySelectorAll('.lj-input--error, .ld-error-input')
        .forEach(el => el.classList.remove('lj-input--error', 'ld-error-input'));
    };

    // jQuery delegation so selectize (category, languages) is covered too — it
    // fires `change` through jQuery, which native capture listeners miss.
    $(root).on('input change', event => clearFor(event.target));
    // Trix emits a native event, not a jQuery one.
    root.addEventListener('trix-change', event => clearFor(event.target), true);
  }
}

document.addEventListener('turbolinks:load', () => {
  if (document.querySelector('.lj-wizard-post')) {
    const ReactRailsUJS = require('react_ujs');

    new PostWizard();

    ReactRailsUJS.mountComponents();

    $(document).on('keydown', '#location-filter', ev => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
      }
    });
  }
});
